import React, { useEffect, useMemo, useState, useCallback } from 'react'
import DocumentTitle from 'react-document-title'
import { PageNavLinks, PageTitle } from '@Root/components'
import { useLocation } from 'react-router'
import { Forms } from '@Root/modules/Forms/Forms'
import { Switch, Route, Redirect } from 'react-router-dom'
import { DetailsCognitoForms } from '@Root/modules/Forms/DetailsCognitoForms'
import { RawSubmissionData } from '@Root/modules/Forms/RawSubmissionData'
import { SubmissionPreview } from '@Root/modules/Forms/SubmissionPreview'
import { MappingsCognitoForms } from '@Root/modules/Forms/MappingsCognitoForms'
import { FormEmails } from '@Root/modules/Forms/FormEmails'
import { generateStyleConfig } from '@Root/pages/Home/Admissions/lib'
import styles from './style.module.scss'
import { FormsChromeContext } from './chrome'

const formSectionLinks = () => [
  { name: 'Submissions', path: '', exact: true },
  { name: 'Mappings', path: 'mappings' },
  { name: 'Emails', path: 'emails' },
]

const submissionLinks = submissionId => [
  { name: 'Form Preview', path: `preview/${submissionId}` },
  { name: 'Raw JSON', path: `details/${submissionId}` },
]

const resolveChrome = pathname => {
  const parts = pathname.split('/')
  const formId = parts[3]
  const section = parts[4]
  const submissionId = parts[5]
  const isSubmission = section === 'preview' || section === 'details'
  const isTablePage = !formId || !section

  if (!formId) {
    return {
      title: 'Cognito Forms',
      root: '/home',
      links: [{ name: 'Cognito Forms', path: 'forms', exact: true }],
      isTablePage,
    }
  }

  if (isSubmission) {
    return {
      title: section === 'preview' ? 'Form Preview' : 'Raw Submission Data',
      root: `/home/forms/${formId}`,
      links: submissionLinks(submissionId),
      isTablePage,
    }
  }

  const titles = { mappings: 'Mappings', emails: 'Emails' }

  return {
    title: titles[section] || 'Submissions',
    root: `/home/forms/${formId}`,
    links: formSectionLinks(),
    isTablePage,
  }
}

export const FormsPage = () => {
  const { pathname } = useLocation()
  const chrome = useMemo(() => resolveChrome(pathname), [pathname])
  const styleConfig = useMemo(() => generateStyleConfig(false), [])
  const [extraLinks, setExtraLinks] = useState([])
  const setLinks = useCallback(links => setExtraLinks(links), [])

  useEffect(() => {
    const isSubmission = pathname.includes('/preview/') || pathname.includes('/details/')
    if (!isSubmission) setExtraLinks([])
  }, [pathname])
  const contextValue = useMemo(() => ({ styleConfig, setExtraLinks: setLinks }), [styleConfig, setLinks])

  return (
    <DocumentTitle title={`${chrome.title} - Forms`}>
      <div className={styles.wrapper}>
        <PageTitle title={chrome.title} style={styleConfig.pageTitle} />
        <PageNavLinks style={styleConfig.navLinksStyles} root={chrome.root} links={[...chrome.links, ...extraLinks]} />
        <FormsChromeContext.Provider value={contextValue}>
          <div className={chrome.isTablePage ? undefined : styles.detailOffset}>
          <Switch>
            <Route path='/home/forms/:id/preview/:submissionId' render={() => <SubmissionPreview />} />
            <Route path='/home/forms/:id/details/:submissionId' render={() => <RawSubmissionData />} />
            <Route path='/home/forms/:id/mappings' render={() => <MappingsCognitoForms />} />
            <Route path='/home/forms/:id/emails' render={() => <FormEmails />} />
            <Route path='/home/forms/:id' render={() => <DetailsCognitoForms styleConfig={styleConfig} />} />
            <Route exact path='/home/forms' render={() => <Forms styleConfig={styleConfig} />} />
            <Redirect to='/home/forms' />
          </Switch>
          </div>
        </FormsChromeContext.Provider>
      </div>
    </DocumentTitle>
  )
}
